/**
 * @file SupplierHaulageRatesPanel.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-09-29
 * @description A supplier's haulage rate card, in the contact window: what the
 *              supplier charges us per pallet to deliver direct, by the
 *              customer's postcode area ("EX") or district ("EX31"), from a
 *              date. Drawn on by Direct Delivery order lines.
 *
 *              A rate change is a new row with a later "From", not an edit of
 *              the old one — an order already raised keeps the rate it was
 *              raised at, and the card keeps the history of what applied when.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Input from './Input';
import DateInput from './DateInput';
import ConfirmDialog from './ConfirmDialog';
import AuditHistoryModal from './AuditHistoryModal';
import HaulageRateImportModal from './HaulageRateImportModal';
import { useSlideOutButton } from './SlideOutButton';
import useAccess from '../hooks/useAccess';
import { formatPrice } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import './SupplierHaulageRatesPanel.css';

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { id: null, postcode_prefix: '', rate: '', effective_from: '', effective_to: '', notes: '' };
const dateOnly = (d) => (d ? String(d).slice(0, 10) : '');

/** Where a rate stands today: in force, scheduled, or ended. */
function stateOf(r) {
  const t = today();
  const from = dateOnly(r.effective_from);
  const to = dateOnly(r.effective_to);
  if (from > t) return 'scheduled';
  if (to && to < t) return 'ended';
  return 'in-force';
}

function SupplierHaulageRatesPanel({ supplierId, supplierName = '' }) {
  const access = useAccess();
  const canEdit = access.can('contacts.edit') && !access.readOnly;
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null); // null = closed; EMPTY-shaped when adding/editing
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  // Reading a rate card needs the server's Claude key; the button only shows
  // where it can work.
  const [importAvailable, setImportAvailable] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [notice, setNotice] = useState('');

  useSlideOutButton([!!form, rates.length, importAvailable]);

  useEffect(() => {
    fetch(`${API_URL}/api/supplier-haulage/extract/status`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setImportAvailable(!!d?.available))
      .catch(() => setImportAvailable(false));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/supplier-haulage/suppliers/${supplierId}/rates`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load rates');
      setRates(data.rates || []);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => { load(); }, [load]);

  const inForce = useMemo(() => rates.filter(r => stateOf(r) === 'in-force').length, [rates]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const url = form.id
        ? `${API_URL}/api/supplier-haulage/rates/${form.id}`
        : `${API_URL}/api/supplier-haulage/suppliers/${supplierId}/rates`;
      const res = await fetch(url, {
        method: form.id ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postcode_prefix: form.postcode_prefix,
          rate: form.rate,
          effective_from: form.effective_from || today(),
          effective_to: form.effective_to || null,
          notes: form.notes
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save the rate');
      setForm(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rate) => {
    setConfirm(null);
    try {
      const res = await fetch(`${API_URL}/api/supplier-haulage/rates/${rate.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not delete the rate');
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const edit = (r) => setForm({
    id: r.id,
    postcode_prefix: r.postcode_prefix,
    rate: String(Number(r.rate).toFixed(2)),
    effective_from: dateOnly(r.effective_from),
    effective_to: dateOnly(r.effective_to),
    notes: r.notes || ''
  });

  // A new rate for the same postcode starts where this one ends: the usual
  // reason to touch a rate is that the supplier has put it up.
  const supersede = (r) => setForm({
    ...EMPTY,
    postcode_prefix: r.postcode_prefix,
    rate: String(Number(r.rate).toFixed(2)),
    effective_from: today()
  });

  return (
    <div className="haulage-rates-panel">
      <div className="panel-toolbar">
        <div className="toolbar-stats">
          {rates.length} rate{rates.length === 1 ? '' : 's'}
          {rates.length > 0 && <> · {inForce} in force</>}
        </div>
        <div className="buttons slideout">
          {importAvailable && (
            <div>
              <button
                className="btn-slideout"
                data-write-action
                onClick={() => { setNotice(''); setImportOpen(true); }}
                disabled={!canEdit || !!form}
                title={canEdit ? 'Read rates from a rate card — screenshot, PDF, spreadsheet or pasted text' : 'You do not have permission to edit contacts'}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">document_scanner</span></div>
                <span className="btn-label">Import</span>
              </button>
            </div>
          )}
          <div>
            <button
              className="btn-slideout"
              data-write-action
              onClick={() => setForm({ ...EMPTY, effective_from: today() })}
              disabled={!canEdit || !!form}
              title={canEdit ? 'Add a haulage rate' : 'You do not have permission to edit contacts'}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
              <span className="btn-label">Add Rate</span>
            </button>
          </div>
        </div>
      </div>

      <p className="haulage-rates-hint">
        £ per pallet this supplier charges to deliver direct, by the customer's postcode.
        A district (EX31) beats its area (EX). A part pallet is charged as a whole one.
      </p>

      {error && <div className="haulage-rates-error">{error}</div>}
      {notice && <div className="haulage-rates-notice">{notice}</div>}

      {form && (
        <div className="haulage-rate-form">
          <div className="form-group">
            <label>Postcode</label>
            <Input
              value={form.postcode_prefix}
              onChange={(e) => setForm({ ...form, postcode_prefix: e.target.value.toUpperCase() })}
              placeholder="EX or EX31"
              maxLength={5}
            />
          </div>
          <div className="form-group">
            <label>£ / pallet</label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.rate}
              onChange={(e) => setForm({ ...form, rate: e.target.value })}
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <DateInput label="From" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
          </div>
          <div className="form-group">
            <DateInput label="To (optional)" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} />
          </div>
          <div className="form-group notes">
            <label>Notes</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. quote ref" />
          </div>
          <div className="haulage-rate-form-actions">
            <button className="btn-icon" onClick={save} disabled={saving} title="Save rate">
              <span className="material-symbols-rounded">{saving ? 'progress_activity' : 'check'}</span>
            </button>
            <button className="btn-icon btn-danger" onClick={() => setForm(null)} disabled={saving} title="Cancel">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="haulage-rates-info">Loading…</div>
      ) : rates.length === 0 ? (
        <div className="haulage-rates-info">No haulage rates for this supplier yet.</div>
      ) : (
        <table className="data-table haulage-rates-table">
          <thead>
            <tr>
              <th>Postcode</th>
              <th className="num">£ / pallet</th>
              <th>From</th>
              <th>To</th>
              <th>Notes</th>
              <th className="actions-col"></th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r) => {
              const state = stateOf(r);
              return (
                <tr key={r.id} className={`rate-${state}`}>
                  <td>
                    <span className="rate-prefix">{r.postcode_prefix}</span>
                    {state !== 'in-force' && <span className={`rate-state ${state}`}>{state === 'scheduled' ? 'Scheduled' : 'Ended'}</span>}
                  </td>
                  <td className="num">{formatPrice(r.rate)}</td>
                  <td>{formatDateShort(r.effective_from)}</td>
                  <td>{r.effective_to ? formatDateShort(r.effective_to) : <span className="muted-dash">-</span>}</td>
                  <td className="rate-notes" title={r.notes || undefined}>{r.notes || ''}</td>
                  <td className="actions-col">
                    <div className="actions-cell">
                      <button className="btn-icon" data-write-action onClick={() => edit(r)}
                        disabled={!canEdit || !!form} title={canEdit ? 'Edit this rate' : 'You do not have permission to edit contacts'}>
                        <span className="material-symbols-rounded">edit</span>
                      </button>
                      <button className="btn-icon" data-write-action onClick={() => supersede(r)}
                        disabled={!canEdit || !!form || state === 'ended'}
                        title={state === 'ended' ? 'This rate has ended' : 'New rate for this postcode from a date'}>
                        <span className="material-symbols-rounded">trending_up</span>
                      </button>
                      <button className="btn-icon" onClick={() => setHistoryFor(r)} title="View change history">
                        <span className="material-symbols-rounded">history</span>
                      </button>
                      <button className="btn-icon btn-danger" data-write-action
                        onClick={() => setConfirm(r)} disabled={!canEdit}
                        title={canEdit ? 'Delete this rate' : 'You do not have permission to edit contacts'}>
                        <span className="material-symbols-rounded">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {importOpen && (
        <HaulageRateImportModal
          supplierId={supplierId}
          supplierName={supplierName}
          onClose={() => { setImportOpen(false); load(); }}
          onSaved={({ created, updated }) => {
            setImportOpen(false);
            load();
            setNotice([
              created ? `${created} rate${created === 1 ? '' : 's'} added` : '',
              updated ? `${updated} updated` : ''
            ].filter(Boolean).join(', ') + ' from the rate card.');
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm
          ? `Delete the ${confirm.postcode_prefix} rate of ${formatPrice(confirm.rate)} from ${formatDateShort(confirm.effective_from)}? Orders already raised keep the rate they were raised at. To stop a rate applying from a date, edit it and set "To" instead.`
          : ''}
        onConfirm={() => remove(confirm)}
        onCancel={() => setConfirm(null)}
      />
      <AuditHistoryModal
        isOpen={!!historyFor}
        onClose={() => setHistoryFor(null)}
        entityType="supplier_haulage_rate"
        entityId={historyFor?.id}
        entityIdentifier={historyFor ? `${historyFor.postcode_prefix} from ${formatDateShort(historyFor.effective_from)}` : null}
      />
    </div>
  );
}

export default SupplierHaulageRatesPanel;
